import { useMemo } from 'react';
import { useSettings } from 'context/SettingsContext';
import KeysTable from './KeysTable';
import {
    flattenDetailsEntries,
    getModeFieldKey,
    parseJsonObject,
    toSafeTemplateKeyName,
    wrapTemplateKey,
    type TemplateKeyRow,
} from '../keysUtils';

interface SpecificationKeysTabProps {
    objectDelails?: unknown;
    onKeySelect?: (key: string) => void;
}

const formatDisplayValue = (raw: unknown): string => {
    if (raw === null || raw === undefined || raw === '') return '';
    if (Array.isArray(raw)) return raw.map((item) => String(item ?? '').trim()).filter(Boolean).join(', ');
    if (typeof raw === 'object') {
        try {
            return JSON.stringify(raw);
        } catch (_e) {
            return '';
        }
    }
    return String(raw);
};

const SpecificationKeysTab = ({ objectDelails, onKeySelect }: SpecificationKeysTabProps) => {
    const { settings, localSettings } = useSettings();

    const modeFields = useMemo(() => {
        if (Array.isArray(localSettings?.modeFields)) return localSettings.modeFields;
        if (Array.isArray(settings?.modeFields)) return settings.modeFields;
        return [];
    }, [localSettings?.modeFields, settings?.modeFields]);

    const rows = useMemo<TemplateKeyRow[]>(() => {
        const details = parseJsonObject(objectDelails) || {};
        const flatDetails = Object.fromEntries(flattenDetailsEntries(details));
        const names = new Set<string>();

        modeFields.forEach((field: { i18nKey?: string; title?: string }, idx: number) => {
            names.add(getModeFieldKey(field, idx));
        });
        Object.keys(flatDetails).forEach((name) => names.add(name));

        return Array.from(names)
            .filter(Boolean)
            .map((name) => {
                const safeName = toSafeTemplateKeyName(name);
                return {
                    // Prefer underscore tokens so Quill does not break dotted keys.
                    key: wrapTemplateKey(safeName),
                    value: formatDisplayValue(flatDetails[name]),
                };
            });
    }, [modeFields, objectDelails]);

    return <KeysTable rows={rows} onKeySelect={onKeySelect} />;
};

export default SpecificationKeysTab;
